import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { subscriptionService } from '../api/subscriptionService';
import { useI18n, translate } from '../i18n';

const WelcomeOfferContext = createContext({ offer: null });
const WelcomeCountdownContext = createContext(0);

export function WelcomeOfferProvider({ children }) {
  const { user, isAuthenticated } = useAuth();
  const userId = user?.id;
  const [snapshot, setSnapshot] = useState(null);
  const [now, setNow] = useState(() => performance.now());

  useEffect(() => {
    setSnapshot(null);
    if (!isAuthenticated || !userId) return undefined;
    let cancelled = false;
    let inFlight = false;
    const refresh = async () => {
      if (inFlight) return;
      inFlight = true;
      const started = performance.now();
      try {
        const offer = await subscriptionService.getWelcomeOffer();
        if (!cancelled) {
          // Use server time and a monotonic clock; local clock edits cannot restart it.
          const duration = Date.parse(offer?.expires_at) - Date.parse(offer?.server_now);
          const remaining = Number.isFinite(duration) ? Math.max(0, duration) : 0;
          setSnapshot({ userId, offer, deadline: started + remaining });
          setNow(performance.now());
        }
      } catch {
        if (!cancelled) setSnapshot(null);
      } finally {
        inFlight = false;
      }
    };
    const onVisible = () => { if (!document.hidden) refresh(); };
    refresh();
    const poll = setInterval(refresh, 15000);
    const tick = setInterval(() => setNow(performance.now()), 1000);
    window.addEventListener('focus', refresh);
    window.addEventListener('subscription-changed', refresh);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      clearInterval(poll);
      clearInterval(tick);
      window.removeEventListener('focus', refresh);
      window.removeEventListener('subscription-changed', refresh);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [isAuthenticated, userId]);

  // Login publishes the token before /users/me finishes. In that interval both
  // IDs can be undefined, which must not be treated as a loaded offer snapshot.
  const remainingSeconds = snapshot && userId && snapshot.userId === userId && isAuthenticated
    ? Math.max(0, Math.ceil((snapshot.deadline - now) / 1000)) : 0;
  const offer = snapshot?.offer?.active && remainingSeconds > 0 ? snapshot.offer : null;
  const value = useMemo(() => ({ offer }), [offer]);
  return <WelcomeOfferContext.Provider value={value}>
    <WelcomeCountdownContext.Provider value={remainingSeconds}>{children}</WelcomeCountdownContext.Provider>
  </WelcomeOfferContext.Provider>;
}

export const useWelcomeOffer = () => useContext(WelcomeOfferContext);
export const useWelcomeCountdown = () => useContext(WelcomeCountdownContext);

export function discountedPrice(price, percentOff) {
  const amount = (Number(price.amount) * (100 - percentOff) / 100).toFixed(2);
  if (price.currencyLabel === 'USD') return `$${amount}`;
  return translate('{amount} MAD', { amount });
}

export function WelcomePrice({ plan, price }) {
  const { offer } = useWelcomeOffer();
  const { t } = useI18n();
  const display = t(price.display);
  if (!offer?.plans.includes(plan)) return display;
  return <span className="welcome-price">
    <span className="welcome-price-original">{display}</span>{' '}
    <span className="welcome-price-amount">{t(discountedPrice(price, offer.percent_off))}</span>
    <span className="welcome-price-terms">{t('First month · {percent}% off', { percent: offer.percent_off })}<br />{t('Then {price}/month', { price: display })}</span>
  </span>;
}
